<template>
  <div class="region-card">
    <div
      class="region-header"
      :class="{ 'compact-region-header': collapsible }"
    >
      <button
        v-if="collapsible"
        type="button"
        class="region-summary-button"
        @click="$emit('toggle', region.id)"
      >
        <span class="region-summary-title">
          {{ formatRegionRangeFn(region) }}
        </span>
        <i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
      </button>

      <template v-else>
        <div class="region-title-group">
          <strong>
            {{ formatRegionRangeFn(region) }}
          </strong>
          <el-tag v-if="showSensorTag" size="mini" effect="plain">
            {{ sensorLabel }}
          </el-tag>
        </div>
        <span v-if="showTimestampHeader" class="region-detail-value">
          {{ timestampRange }}
        </span>
      </template>

      <el-button
        v-if="showRemove"
        type="text"
        @click="$emit('remove', region.id)"
      >
        {{ removeLabel }}
      </el-button>
    </div>

    <div v-if="showDetails" class="region-details">
      <div v-if="showSensorRow" class="region-detail-row">
        <div class="region-detail-value">
          {{ sensorLabel }}
        </div>
      </div>

      <div v-if="showTimestampRow" class="region-detail-row">
        <div class="region-detail-value">
          {{ timestampRange }}
        </div>
      </div>

      <div class="region-detail-row">
        <div v-if="hasReasonCodes" class="region-tags">
          <el-tag
            v-for="reason in region.reasonCodes"
            :key="region.id + '-tag-' + reason"
            size="mini"
          >
            {{ formatReasonCodeFn(reason) }}
          </el-tag>
        </div>
        <div v-else class="region-detail-value">--</div>
      </div>

      <div v-if="region.note" class="region-detail-row">
        <div class="region-detail-value region-note-detail">
          {{ region.note }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegionSummaryCard',
  props: {
    region: {
      type: Object,
      required: true
    },
    formatRegionRangeFn: {
      type: Function,
      required: true
    },
    formatRegionTimestampFn: {
      type: Function,
      required: true
    },
    timestampCycle: {
      type: Object,
      default: null
    },
    formatReasonCodeFn: {
      type: Function,
      required: true
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: true
    },
    showRemove: {
      type: Boolean,
      default: false
    },
    showSensorTag: {
      type: Boolean,
      default: true
    },
    showSensorRow: {
      type: Boolean,
      default: false
    },
    showTimestampHeader: {
      type: Boolean,
      default: false
    },
    showTimestampRow: {
      type: Boolean,
      default: true
    },
    removeLabel: {
      type: String,
      default: 'Remove'
    }
  },
  computed: {
    sensorLabel() {
      return this.region.sensor || 'BOTH'
    },
    timestampRange() {
      return (
        this.formatRegionTimestampFn(
          this.timestampCycle,
          this.region.startTime,
          this.region.startSec
        ) +
        ' - ' +
        this.formatRegionTimestampFn(
          this.timestampCycle,
          this.region.endTime,
          this.region.endSec
        )
      )
    },
    hasReasonCodes() {
      return !!(
        this.region.reasonCodes &&
        Array.isArray(this.region.reasonCodes) &&
        this.region.reasonCodes.length
      )
    },
    showDetails() {
      return this.collapsible ? this.expanded : true
    }
  }
}
</script>

<style lang="scss" scoped>
.region-card {
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg);

  .el-button {
    padding: 0;
  }
}

.region-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.compact-region-header {
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
}

.region-summary-button {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text);
  cursor: pointer;
  text-align: left;
}

.region-summary-title {
  min-width: 0;
  overflow: hidden;
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.region-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.region-details {
  display: grid;
  gap: 6px;
  margin-top: 8px;
}

.region-detail-row {
  display: grid;
  gap: 4px;
}

.region-detail-value {
  color: var(--text2);
  font-size: 12px;
  line-height: 1.5;
}

.region-note-detail {
  word-break: break-word;
}
</style>
